import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storageAgreement } from '@/mocks/perform';
import { supabase } from '@/lib/supabase';

interface GoldAsset {
  id: string;
  product: string;
  code: string;
  weight: number;
  location: string;
  status: string;
}

export default function StoragePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'agreement' | 'sign' | 'pay'>('agreement');
  const [agreed, setAgreed] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [canConfirm, setCanConfirm] = useState(false);
  const [signature, setSignature] = useState(false);
  const [checkingAgreement, setCheckingAgreement] = useState(true);
  const [goldAssets, setGoldAssets] = useState<GoldAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // 页面加载时检查用户是否已经同意过寄存协议
  useEffect(() => {
    const checkAgreement = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setCheckingAgreement(false);
          return;
        }
        const { data } = await supabase
          .from('profiles')
          .select('storage_agreed')
          .eq('id', user.id)
          .maybeSingle();
        if (data?.storage_agreed) {
          // 已同意过协议，直接跳到签名步骤
          setStep('sign');
        }
      } catch {
        // 出错时保持默认的协议步骤
      } finally {
        setCheckingAgreement(false);
      }
    };
    checkAgreement();
  }, []);

  // 监听页面整体滚动计算阅读进度
  useEffect(() => {
    if (step !== 'agreement') return;
    const checkScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const maxScroll = docHeight - winHeight;
      if (maxScroll <= 10) {
        setReadProgress(100);
        setCanConfirm(true);
        return;
      }
      const progress = Math.min((scrollTop / maxScroll) * 100, 100);
      setReadProgress(progress);
      setCanConfirm(progress >= 95);
    };
    window.addEventListener('scroll', checkScroll);
    // 延迟一次确保 DOM 完全渲染后尺寸正确
    const timer = setTimeout(checkScroll, 150);
    return () => {
      window.removeEventListener('scroll', checkScroll);
      clearTimeout(timer);
    };
  }, [step]);

  // IntersectionObserver 保底：协议底部进入视口时解锁
  useEffect(() => {
    if (step !== 'agreement') return;
    const el = bottomRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setReadProgress(100);
            setCanConfirm(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [step]);

  const handleAgreementConfirm = useCallback(async () => {
    if (!canConfirm) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ storage_agreed: true })
          .eq('id', user.id);
      }
    } catch {
      // 静默失败，继续流程
    }
    setAgreed(true);
    setStep('sign');
  }, [canConfirm]);

  const handleSignConfirm = useCallback(() => {
    if (!signature) return;
    setStep('pay');
  }, [signature]);

  // 进入确认步骤时加载用户的真实金库资产
  useEffect(() => {
    if (step !== 'pay') return;
    const fetchAssets = async () => {
      setLoadingAssets(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setGoldAssets([]);
          return;
        }
        const { data } = await supabase
          .from('gold_assets')
          .select('*')
          .eq('user_id', user.id)
          .neq('status', '已提取')
          .neq('status', '已回购')
          .order('created_at', { ascending: false });
        setGoldAssets(
          (data || []).map((row) => ({
            id: row.id,
            product: row.product || 'AU 999.9 投资金条',
            code: row.code || '-',
            weight: Number(row.weight) || 0,
            location: row.location || '北京金库',
            status: row.status || '存储中',
          })),
        );
      } catch {
        setGoldAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, [step]);

  const handlePay = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // 创建寄存记录到 gold_assets 表（仅当没有真实资产时兜底）
      if (goldAssets.length === 0) {
        const now = new Date();
        const storeDate = now.toISOString().slice(0, 10);
        const { error } = await supabase.from('gold_assets').insert({
          user_id: user.id,
          product: 'AU 999.9 投资金条',
          code: `AU9999-${storeDate.replace(/-/g, '')}-${String(Math.floor(Math.random() * 999)).padStart(3, '0')}`,
          weight: 100,
          status: '存储中',
          store_date: storeDate,
          location: '北京金库',
        });
        if (error) {
          // eslint-disable-next-line no-console
          console.error('寄存记录创建失败:', error);
        }
      }
    } catch {
      // 静默失败
    }

    navigate('/orders/storage/records');
  }, [navigate, goldAssets]);

  if (checkingAgreement) {
    return (
      <div className="min-h-screen bg-brown-50 flex items-center justify-center">
        <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-sm font-bold">黄金寄存服务</h1>
        <div className="w-8" />
      </div>

      {/* Step Indicator */}
      <div className="bg-white px-4 py-4">
        <div className="flex items-center justify-between relative">
          {['阅读协议', '电子签名', '确认存入'].map((label, idx) => {
            const stepKeys = ['agreement', 'sign', 'pay'] as const;
            const currentIdx = stepKeys.indexOf(step);
            const active = idx <= currentIdx;
            const current = idx === currentIdx;
            return (
              <div key={label} className="flex flex-col items-center relative z-10">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  active ? 'bg-gold-500 text-brown-950 border-gold-500' : 'bg-white text-gray-400 border-gray-300'
                } ${current ? 'ring-2 ring-gold-300' : ''}`}>
                  {idx + 1}
                </div>
                <span className={`text-[10px] mt-1.5 font-medium ${active ? 'text-gold-600' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
            );
          })}
          <div className="absolute top-3.5 left-4 right-4 h-0.5 bg-gray-200 -z-0" />
          <div
            className="absolute top-3.5 left-4 h-0.5 bg-gold-500 -z-0 transition-all"
            style={{ width: `${(['agreement', 'sign', 'pay'] as const).indexOf(step) * 50}%` }}
          />
        </div>
      </div>

      {/* Agreement Step */}
      {step === 'agreement' && (
        <>
          <div className="px-4 py-2 flex items-center justify-between bg-white border-b border-gray-100">
            <span className="text-gray-400 text-[10px]">阅读进度</span>
            <span className={`text-[10px] font-bold ${readProgress >= 95 ? 'text-green-500' : 'text-amber-600'}`}>
              {Math.round(readProgress)}%
            </span>
          </div>
          <div className="w-full h-1 bg-gray-100">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-gold-500 rounded-r-full transition-all"
              style={{ width: `${readProgress}%` }}
            />
          </div>
          <div className="px-5 pt-4 pb-32">
            <div className="bg-amber-50 rounded-xl p-4 mb-4 border border-amber-100">
              <div className="flex items-center gap-2">
                <i className="ri-alarm-warning-line text-amber-600 text-lg"></i>
                <span className="text-amber-800 text-sm font-bold">重要提示</span>
              </div>
              <p className="text-amber-700 text-xs mt-2 leading-relaxed">
                为充分保障您的合法权益，请仔细阅读以下寄存服务协议内容。您需要完整阅读至底部，方可确认已阅读并同意。
              </p>
            </div>
            <h2 className="text-brown-900 text-base font-bold text-center mb-4">{storageAgreement.title}</h2>
            {storageAgreement.sections.map((section, idx) => (
              <div key={idx} className="mb-4">
                <h3 className="text-brown-900 text-sm font-bold mb-1.5">{section.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{section.body}</p>
              </div>
            ))}
            <div ref={bottomRef} className="h-2" />
            {/* 底部占位确保内容足够长，能触发页面滚动 */}
            <div className="h-16" />
          </div>
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pt-3 pb-5 z-40">
            <button
              onClick={handleAgreementConfirm}
              disabled={!canConfirm}
              className={`w-full py-3 rounded-xl text-white text-base font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all ${
                canConfirm
                  ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {canConfirm ? (
                <>
                  <i className="ri-check-double-line"></i>
                  我已阅读并同意
                </>
              ) : (
                <>
                  <i className="ri-scroll-to-bottom-line"></i>
                  请继续阅读至底部
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Sign Step */}
      {step === 'sign' && (
        <div className="px-4 pt-6 pb-24">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-center mb-5">
              <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-3">
                <i className="ri-edit-line text-amber-500 text-2xl"></i>
              </div>
              <h3 className="text-brown-900 text-base font-bold">电子签名</h3>
              <p className="text-gray-400 text-xs mt-1">请勾选确认以完成电子签名</p>
            </div>
            <button
              onClick={() => setSignature(!signature)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border text-left cursor-pointer transition-all ${
                signature ? 'border-gold-500 bg-amber-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className={`w-6 h-6 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                signature ? 'bg-gold-500 border-gold-500' : 'border-gray-300'
              }`}>
                {signature && <i className="ri-check-line text-white text-xs"></i>}
              </div>
              <div className="flex-1">
                <p className="text-brown-900 text-sm font-medium">本人确认并同意</p>
                <p className="text-gray-400 text-xs mt-0.5">我确认已阅读并同意《黄金寄存服务协议》全部条款，自愿接受协议约束。</p>
              </div>
            </button>
          </div>
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pt-3 pb-5 z-40">
            <button
              onClick={handleSignConfirm}
              disabled={!signature}
              className={`w-full py-3 rounded-xl text-white text-base font-semibold cursor-pointer transition-all ${
                signature
                  ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              确认签名
            </button>
          </div>
        </div>
      )}

      {/* Pay Step — 寄存无需再付款，直接确认完成 */}
      {step === 'pay' && (
        <div className="px-4 pt-6 pb-24">
          {loadingAssets ? (
            <div className="flex items-center justify-center py-16">
              <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
            </div>
          ) : goldAssets.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-4">
                <i className="ri-safe-2-line text-amber-500 text-3xl"></i>
              </div>
              <h3 className="text-brown-900 text-lg font-bold">确认存入金库</h3>
              <p className="text-gray-500 text-sm mt-2">
                您购买的黄金实物已完成全部款项支付，现申请存入金库托管。
              </p>
              <div className="mt-4 bg-amber-50 rounded-lg p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">产品</span>
                  <span className="text-brown-900 font-medium">AU 999.9 投资金条</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">重量</span>
                  <span className="text-brown-900 font-medium">100 克</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">金库位置</span>
                  <span className="text-brown-900 font-medium">北京金库</span>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={handlePay}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-white text-base font-semibold flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
                >
                  <i className="ri-check-double-line"></i>
                  确认存入金库
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-5 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-3">
                  <i className="ri-safe-2-line text-amber-500 text-2xl"></i>
                </div>
                <h3 className="text-brown-900 text-base font-bold">确认存入金库</h3>
                <p className="text-gray-400 text-xs mt-1">
                  请选择要存入金库的黄金资产
                </p>
              </div>
              {goldAssets.map((asset) => (
                <div key={asset.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-vip-diamond-fill text-brown-950 text-xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-brown-900 text-sm font-semibold truncate">{asset.product}</h4>
                      <p className="text-brown-400 text-xs mt-0.5">{asset.weight}g · {asset.code}</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-left space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">重量</span>
                      <span className="text-brown-900 font-medium">{asset.weight} 克</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">金库位置</span>
                      <span className="text-brown-900 font-medium">{asset.location}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">溯源编码</span>
                      <span className="text-brown-900 font-medium">{asset.code}</span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="mt-2">
                <button
                  onClick={handlePay}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-white text-base font-semibold flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
                >
                  <i className="ri-check-double-line"></i>
                  确认存入金库
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}